import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../../api';
import { showToast } from '../../components/ui/toast';
import { icon } from '../../components/ui/icons';
import { t } from '../../i18n';
import { mediaUploadQuery, isImageKey } from '../../media-meta';
import { UploadQueue, type UploadTask } from '../../upload-queue';
import { saveZip, type ZipEntry } from '../../zip';

interface Folder { id: string; name: string; }
interface Album {
  id: string; name: string; folder_id: string | null; description: string | null;
  item_count?: number; cover_url?: string | null; cover_type?: 'photo' | 'video' | null;
  created_at?: string; last_activity_at?: string | null; taken_min?: string | null; taken_max?: string | null;
}
interface Item { id: string; type: 'photo' | 'video'; file_url: string; thumbnail_url: string | null; original_filename: string; duration: number | null; }

const mediaUrl = (key: string | null | undefined) => (key ? `/api/v1/media/${key}` : '');
function fmtDuration(s: number | null): string {
  if (s == null) return '';
  const m = Math.floor(s / 60), sec = s % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

/**
 * <cadence-portal-gallery> — student view of the media gallery. Browse approved albums
 * and items, and submit your own photos/videos (which land in the approval queue).
 */
@customElement('cadence-portal-gallery')
export class CadencePortalGallery extends LitElement {
  @state() private loading = true;
  @state() private folders: Folder[] = [];
  @state() private albums: Album[] = [];
  @state() private activeFolder = 'all';
  @state() private dateBasis: 'updated' | 'taken' = 'updated';
  @state() private dateFrom = '';
  @state() private dateTo = '';
  @state() private openAlbum: Album | null = null;
  @state() private items: Item[] = [];
  @state() private albumLoading = false;
  @state() private uploadTasks: UploadTask[] = [];
  private uploadQueue?: UploadQueue;
  @state() private lightbox: Item | null = null;
  @state() private lbFull = false; // false ⇒ show the cached thumbnail; true ⇒ full image loaded

  // Selection (Google Photos-style) + download (no remove in the portal)
  @state() private selected = new Set<string>();
  private lastIndex = -1;
  @state() private zip: { total: number; done: number; name: string } | null = null;

  static styles = css`
    :host { display: block; }
    h1 { margin: 0 0 var(--space-4); font-size: 1.5rem; }
    .bar { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: var(--space-4); }
    .chip { padding: 4px 12px; border-radius: 999px; border: 1px solid var(--color-border); background: var(--color-bg-primary); color: var(--color-text-secondary); cursor: pointer; font-size: 0.82rem; display: inline-flex; align-items: center; gap: 6px; }
    .chip.active { background: var(--color-primary); color: #fff; border-color: var(--color-primary); }
    .filterbar { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: var(--space-4); font-size: 0.85rem; }
    .filterbar .fl { color: var(--color-text-secondary); }
    .filterbar select, .filterbar input { font: inherit; font-size: 0.85rem; padding: 5px 8px; border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg-primary); color: var(--color-text-primary); }
    .filterbar .clear { background: none; border: none; color: var(--color-primary); cursor: pointer; font: inherit; font-size: 0.85rem; padding: 5px 4px; }
    .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); gap: 14px; }
    .album-card { border: 1px solid var(--color-border); border-radius: var(--radius-md); overflow: hidden; cursor: pointer; background: var(--color-bg-primary); }
    .album-card:hover { border-color: var(--color-primary); }
    .album-cover { aspect-ratio: 4/3; background: var(--color-bg-secondary); display: grid; place-items: center; color: var(--color-text-secondary); overflow: hidden; }
    .album-cover { position: relative; }
    .album-cover img, .album-cover video { width: 100%; height: 100%; object-fit: cover; }
    .album-cover svg { width: 36px; height: 36px; opacity: 0.5; }
    .album-cover .cover-play { position: absolute; inset: 0; display: grid; place-items: center; pointer-events: none; }
    .album-cover .cover-play span { background: rgba(0,0,0,0.5); color: #fff; border-radius: 999px; width: 34px; height: 34px; display: grid; place-items: center; }
    .album-cover .cover-play svg { width: 18px; height: 18px; opacity: 1; }
    .album-meta { padding: 10px 12px; }
    .album-meta .name { font-weight: 600; word-break: break-word; }
    .album-meta .sub { color: var(--color-text-secondary); font-size: 0.78rem; margin-top: 2px; }
    .item-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 10px; }
    .item-tile { position: relative; aspect-ratio: 1; border-radius: var(--radius-md); overflow: hidden; background: var(--color-bg-secondary); cursor: pointer; border: 1px solid var(--color-border); }
    .item-tile img, .item-tile video { width: 100%; height: 100%; object-fit: cover; display: block; }
    .vid-badge { position: absolute; inset: 0; display: grid; place-items: center; pointer-events: none; }
    .vid-badge .pill { background: rgba(0,0,0,0.55); color: #fff; border-radius: 999px; padding: 6px 8px; display: inline-flex; align-items: center; gap: 4px; font-size: 0.72rem; }
    .vid-badge svg { width: 16px; height: 16px; }
    .item-tile img, .item-tile video { transition: transform 0.12s ease; }
    .item-tile.selected { background: var(--color-bg-secondary); }
    .item-tile.selected img, .item-tile.selected video { transform: scale(0.85); border-radius: 8px; box-shadow: 0 0 0 3px var(--color-primary); }
    .sel-check { position: absolute; top: 6px; left: 6px; width: 22px; height: 22px; padding: 0; border-radius: 50%; background: rgba(255,255,255,0.6); border: 2px solid rgba(255,255,255,0.9); box-shadow: 0 0 2px rgba(0,0,0,0.4); display: grid; place-items: center; cursor: pointer; opacity: 0; transition: opacity 0.12s; z-index: 2; color: rgba(0,0,0,0.45); }
    .item-tile:hover .sel-check { opacity: 1; }
    .item-grid.selecting .sel-check { opacity: 1; }
    .sel-check:hover { background: #fff; border-color: #fff; color: rgba(0,0,0,0.75); opacity: 1; }
    .sel-check.on { background: var(--color-primary); border-color: var(--color-primary); opacity: 1; color: #fff; }
    .sel-check svg { width: 14px; height: 14px; }
    .sel-bar { display: flex; align-items: center; gap: 10px; margin-bottom: var(--space-4); padding: 8px 10px; border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg-secondary); }
    .sel-bar .sel-count { font-weight: 600; font-size: 0.9rem; }
    .sel-bar .sel-link { background: none; border: none; color: var(--color-primary); cursor: pointer; font: inherit; font-size: 0.85rem; }
    .sel-bar .sel-spacer { flex: 1; }
    .sel-bar .icon-btn { background: none; border: none; cursor: pointer; color: var(--color-text-secondary); display: inline-flex; padding: 2px; }
    .sel-bar .icon-btn:hover { color: var(--color-text-primary); }
    .zip-bar { margin-bottom: var(--space-4); padding: 8px 10px; border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg-primary); font-size: 0.85rem; }
    .zip-track { height: 6px; border-radius: 999px; background: var(--color-bg-secondary); overflow: hidden; margin-top: 6px; }
    .zip-track > span { display: block; height: 100%; background: var(--color-primary); transition: width 0.15s ease; }
    .back { display: inline-flex; align-items: center; gap: 4px; background: none; border: none; cursor: pointer; color: var(--color-text-secondary); font: inherit; padding: 4px 0; margin-bottom: 6px; }
    .back:hover { color: var(--color-primary); }
    .album-head h1 { font-size: 1.3rem; margin: 0; }
    .album-head .desc { color: var(--color-text-secondary); font-size: 0.9rem; margin: 4px 0 0; }
    .submit-wrap { margin: var(--space-4) 0; }
    .submit-note { font-size: 0.8rem; color: var(--color-text-secondary); margin-bottom: 6px; }
    .lb-stage { position: relative; display: grid; place-items: center; background: var(--color-bg-secondary); border-radius: var(--radius-md); max-height: 60vh; overflow: hidden; }
    .lb-stage img, .lb-stage video { max-width: 100%; max-height: 60vh; object-fit: contain; }
    .lb-preload { position: absolute; width: 1px; height: 1px; opacity: 0; pointer-events: none; }
    .lb-footer { display: flex; flex: 1; flex-wrap: wrap; gap: 10px; align-items: center; justify-content: flex-end; }
  `;

  connectedCallback(): void { super.connectedCallback(); this.load(); }

  private async load() {
    this.loading = true;
    try {
      const res = await api.get<{ folders: Folder[]; albums: Album[] }>('/portal/gallery');
      this.folders = res.folders; this.albums = res.albums;
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.loading = false; }
  }

  private get visibleAlbums(): Album[] {
    let list = this.albums;
    if (this.activeFolder === 'unfiled') list = list.filter((a) => !a.folder_id);
    else if (this.activeFolder !== 'all') list = list.filter((a) => a.folder_id === this.activeFolder);
    if (this.dateFrom || this.dateTo) list = list.filter((a) => this.albumMatchesDate(a));
    return list;
  }

  private albumMatchesDate(a: Album): boolean {
    const from = this.dateFrom, to = this.dateTo;
    if (this.dateBasis === 'taken') {
      const lo = (a.taken_min ?? '').slice(0, 10), hi = (a.taken_max ?? '').slice(0, 10);
      if (!lo || !hi) return false;
      if (from && hi < from) return false;
      if (to && lo > to) return false;
      return true;
    }
    const d = (a.last_activity_at ?? a.created_at ?? '').slice(0, 10);
    if (!d) return false;
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  }

  private clearDateFilter() { this.dateFrom = ''; this.dateTo = ''; }

  private async openAlbumView(a: Album) {
    this.openAlbum = a; this.albumLoading = true; this.items = []; this.clearSelection();
    try {
      const res = await api.get<{ album: Album; items: Item[] }>(`/portal/gallery/albums/${a.id}`);
      this.openAlbum = res.album; this.items = res.items;
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.albumLoading = false; }
  }

  // ---- Selection (Google Photos-style) + bulk download ----
  private get selectionActive(): boolean { return this.selected.size > 0; }
  private clearSelection() { this.selected = new Set(); this.lastIndex = -1; }
  private selectAllItems() { this.selected = new Set(this.items.map((i) => i.id)); }
  private toggleSelect(index: number, shift: boolean) {
    const sel = new Set(this.selected);
    if (shift && this.lastIndex >= 0) {
      const [a, b] = this.lastIndex < index ? [this.lastIndex, index] : [index, this.lastIndex];
      for (let i = a; i <= b; i++) sel.add(this.items[i].id);
    } else {
      const id = this.items[index].id;
      if (sel.has(id)) sel.delete(id); else sel.add(id);
      this.lastIndex = index;
    }
    this.selected = sel;
  }
  private onTileClick(e: MouseEvent, index: number) {
    if (this.selectionActive) this.toggleSelect(index, e.shiftKey);
    else this.openLightbox(this.items[index]);
  }

  /** Bundle the selection into one ZIP via the native Save dialog (no "edit files" prompt).
   *  Files stream from R2 through the browser-side zip writer — no Worker memory/timeout. */
  private async downloadSelected() {
    const sel = this.items.filter((it) => this.selected.has(it.id));
    if (!sel.length) return;
    const entries: ZipEntry[] = sel.map((it) => ({
      name: it.original_filename || it.id,
      stream: async () => {
        const r = await fetch(mediaUrl(it.file_url), { credentials: 'include' });
        if (!r.ok || !r.body) throw new Error(t('portal.toast.fetchFailed', { name: it.original_filename || it.id }));
        return r.body;
      },
    }));
    const zipName = `${(this.openAlbum?.name || 'media').replace(/[\\/:*?"<>|]/g, '_')}.zip`;
    this.zip = { total: entries.length, done: 0, name: '' };
    try {
      const res = await saveZip(zipName, entries, (i, n, phase) => {
        this.zip = { total: entries.length, done: phase === 'done' ? i + 1 : i, name: n };
      });
      if (res === 'saved') {
        showToast(entries.length === 1
          ? t('portal.toast.downloadedOne', { count: entries.length, name: zipName })
          : t('portal.toast.downloadedOther', { count: entries.length, name: zipName }), 'success');
        this.clearSelection();
      }
    } catch (e) {
      showToast(t('portal.toast.downloadFailed', { message: (e as Error).message }), 'error');
    } finally {
      this.zip = null;
    }
  }

  private ensureQueue(): UploadQueue {
    if (this.uploadQueue) return this.uploadQueue;
    let prevDone = 0;
    this.uploadQueue = new UploadQueue({
      concurrency: 3,
      buildPath: (file, meta, albumId) => `/portal/gallery/albums/${albumId}/items?${mediaUploadQuery(file, meta)}`,
      onUploaded: async (item: any, _file, meta) => {
        if (meta.thumbnailBlob && item?.id) await api.upload(`/portal/gallery/items/${item.id}/thumbnail`, meta.thumbnailBlob);
      },
      onChange: () => { this.uploadTasks = [...this.uploadQueue!.tasks]; },
      onBatchComplete: () => {
        const done = this.uploadTasks.filter((task) => task.status === 'done').length;
        const failed = this.uploadTasks.filter((task) => task.status === 'error').length;
        if (done > prevDone) {
          const base = done === 1 ? t('portal.toast.submittedOne', { count: done }) : t('portal.toast.submittedOther', { count: done });
          showToast(`${base}${failed ? t('portal.toast.failedSuffix', { count: failed }) : ''}`, failed ? 'error' : 'success');
        }
        prevDone = done;
      },
      onUnsupported: (f) => showToast(t('portal.toast.skippedUnsupported', { name: f.name }), 'error'),
    });
    return this.uploadQueue;
  }
  private onFiles(files: File[]) {
    if (!this.openAlbum) return;
    this.ensureQueue().add(files, this.openAlbum.id);
  }

  render() {
    if (this.loading) return html`<cadence-loading></cadence-loading>`;
    return html`
      <h1>${t('common.portalNav.gallery')}</h1>
      ${this.openAlbum ? this.renderAlbumView() : this.renderAlbumsList()}
      ${this.renderLightbox()}
    `;
  }

  /** Album cover: photo → <img>; video-only album → its first frame via <video>. */
  private renderCover(a: Album) {
    if (!a.cover_url) return icon('image', 36);
    const inner = isImageKey(a.cover_url)
      ? html`<img src=${mediaUrl(a.cover_url)} alt=${a.name} loading="lazy" />`
      : html`<video src=${mediaUrl(a.cover_url)} preload="metadata" muted></video>`;
    return html`${inner}${a.cover_type === 'video' ? html`<div class="cover-play"><span>${icon('play', 18)}</span></div>` : ''}`;
  }

  private renderAlbumsList() {
    const hasUnfiled = this.albums.some((a) => !a.folder_id);
    if (!this.albums.length) return html`<cadence-empty-state heading=${t('portal.gallery.noMedia')} message=${t('portal.gallery.noMediaMessage')}></cadence-empty-state>`;
    return html`
      ${this.folders.length || hasUnfiled ? html`
        <div class="bar">
          <span class="chip ${this.activeFolder === 'all' ? 'active' : ''}" @click=${() => (this.activeFolder = 'all')}>${t('portal.gallery.all')}</span>
          ${this.folders.map((f) => html`<span class="chip ${this.activeFolder === f.id ? 'active' : ''}" @click=${() => (this.activeFolder = f.id)}>${icon('folder', 14)} ${f.name}</span>`)}
          ${hasUnfiled ? html`<span class="chip ${this.activeFolder === 'unfiled' ? 'active' : ''}" @click=${() => (this.activeFolder = 'unfiled')}>${t('portal.gallery.unfiled')}</span>` : ''}
        </div>` : ''}
      ${this.renderDateFilter()}
      ${this.visibleAlbums.length ? html`
        <div class="grid">
          ${this.visibleAlbums.map((a) => html`
            <div class="album-card" @click=${() => this.openAlbumView(a)}>
              <div class="album-cover">${this.renderCover(a)}</div>
              <div class="album-meta">
                <div class="name">${a.name}</div>
                <div class="sub">${(a.item_count ?? 0) === 1 ? t('portal.gallery.itemOne', { count: a.item_count ?? 0 }) : t('portal.gallery.itemOther', { count: a.item_count ?? 0 })}</div>
              </div>
            </div>`)}
        </div>`
        : html`<cadence-empty-state heading=${t('portal.gallery.noAlbumsMatch')} message=${t('portal.gallery.noAlbumsMatchMessage')}></cadence-empty-state>`}
    `;
  }

  private renderDateFilter() {
    return html`
      <div class="filterbar">
        <span class="fl">${t('portal.gallery.filterBy')}</span>
        <select class="basis" .value=${this.dateBasis} @change=${(e: Event) => (this.dateBasis = (e.target as HTMLSelectElement).value as 'updated' | 'taken')}>
          <option value="updated">${t('portal.gallery.lastUpdated')}</option>
          <option value="taken">${t('portal.gallery.dateTaken')}</option>
        </select>
        <input type="date" .value=${this.dateFrom} @change=${(e: Event) => (this.dateFrom = (e.target as HTMLInputElement).value)} aria-label=${t('portal.gallery.fromDate')} />
        <span class="fl">${t('portal.gallery.to')}</span>
        <input type="date" .value=${this.dateTo} @change=${(e: Event) => (this.dateTo = (e.target as HTMLInputElement).value)} aria-label=${t('portal.gallery.toDate')} />
        ${this.dateFrom || this.dateTo ? html`<button class="clear" @click=${() => this.clearDateFilter()}>${t('portal.gallery.clear')}</button>` : ''}
      </div>`;
  }

  private renderAlbumView() {
    const a = this.openAlbum!;
    return html`
      <button class="back" @click=${() => { this.openAlbum = null; this.items = []; this.clearSelection(); }}>${icon('chevron-left', 16)} ${t('portal.gallery.allAlbums')}</button>
      <div class="album-head">
        <h1>${a.name}</h1>
        ${a.description ? html`<p class="desc">${a.description}</p>` : ''}
      </div>
      <div class="submit-wrap">
        <div class="submit-note">${t('portal.gallery.submitNote')}</div>
        <cadence-file-upload multiple
          label=${t('portal.gallery.dropLabel')}
          @files=${(e: CustomEvent) => this.onFiles(e.detail)}></cadence-file-upload>
        ${this.uploadTasks.length ? html`
          <div style="margin-top:12px">
            <cadence-upload-queue .tasks=${this.uploadTasks}
              @cancel=${(e: CustomEvent) => this.uploadQueue?.cancel(e.detail)}
              @retry=${(e: CustomEvent) => this.uploadQueue?.retry(e.detail)}
              @clear=${() => this.uploadQueue?.clearFinished()}></cadence-upload-queue>
          </div>` : ''}
      </div>
      ${this.selectionActive ? html`
        <div class="sel-bar">
          <button class="icon-btn" title=${t('portal.gallery.clearSelection')} @click=${() => this.clearSelection()}>${icon('x', 18)}</button>
          <span class="sel-count">${t('portal.gallery.selectedCount', { count: this.selected.size })}</span>
          <button class="sel-link" @click=${() => this.selectAllItems()}>${t('portal.gallery.selectAll', { count: this.items.length })}</button>
          <span class="sel-spacer"></span>
          <cadence-button size="sm" @click=${() => this.downloadSelected()}>${icon('download', 16)} ${t('portal.gallery.download')}</cadence-button>
        </div>` : ''}
      ${this.zip ? html`
        <div class="zip-bar">
          <span>${t('portal.gallery.preparingZip')} — ${this.zip.done}/${this.zip.total}${this.zip.name ? ` · ${this.zip.name}` : ''}</span>
          <div class="zip-track"><span style="width:${Math.round((this.zip.done / Math.max(1, this.zip.total)) * 100)}%"></span></div>
        </div>` : ''}
      ${this.albumLoading ? html`<cadence-loading></cadence-loading>`
        : this.items.length ? html`<div class="item-grid ${this.selectionActive ? 'selecting' : ''}">${this.items.map((it, i) => this.renderTile(it, i))}</div>`
        : html`<cadence-empty-state heading=${t('portal.gallery.emptyAlbum')} message=${t('portal.gallery.emptyAlbumMessage')}></cadence-empty-state>`}
    `;
  }

  private renderTile(it: Item, index: number) {
    const usePoster = it.type === 'photo' || isImageKey(it.thumbnail_url);
    const sel = this.selected.has(it.id);
    return html`
      <div class="item-tile ${sel ? 'selected' : ''}" @click=${(e: MouseEvent) => this.onTileClick(e, index)}>
        <button class="sel-check ${sel ? 'on' : ''}" title=${t('portal.gallery.select')}
          @click=${(e: MouseEvent) => { e.stopPropagation(); this.toggleSelect(index, e.shiftKey); }}>${icon('check', 14)}</button>
        ${usePoster
          ? html`<img src=${mediaUrl(it.thumbnail_url || it.file_url)} alt=${it.original_filename} loading="lazy" />`
          : html`<video src=${mediaUrl(it.file_url)} preload="metadata" muted></video>`}
        ${it.type === 'video' ? html`<div class="vid-badge"><span class="pill">${icon('play', 16)} ${fmtDuration(it.duration)}</span></div>` : ''}
      </div>`;
  }

  private openLightbox(it: Item) { this.lightbox = it; this.lbFull = false; }

  private downloadItem(it: Item) {
    const a = document.createElement('a');
    a.href = mediaUrl(it.file_url);
    a.download = it.original_filename || 'download';
    document.body.appendChild(a); a.click(); a.remove();
  }
  private openItem(it: Item) { window.open(mediaUrl(it.file_url), '_blank', 'noopener'); }

  private renderLightbox() {
    const it = this.lightbox;
    if (!it) return '';
    return html`
      <cadence-modal ?open=${!!it} heading=${it.original_filename} @close=${() => (this.lightbox = null)}>
        <div class="lb-stage">
          ${it.type === 'photo'
            ? html`
                <img class="lb-img ${this.lbFull ? '' : 'thumb'}"
                  src=${this.lbFull ? mediaUrl(it.file_url) : mediaUrl(it.thumbnail_url || it.file_url)}
                  alt=${it.original_filename} />
                ${!this.lbFull && it.thumbnail_url && it.thumbnail_url !== it.file_url
                  ? html`<img class="lb-preload" src=${mediaUrl(it.file_url)} @load=${() => (this.lbFull = true)} aria-hidden="true" />`
                  : ''}`
            : html`<video src=${mediaUrl(it.file_url)} controls preload="metadata"></video>`}
        </div>
        <div slot="footer" class="lb-footer">
          <cadence-button variant="ghost" @click=${() => this.openItem(it)}>${t('portal.gallery.openFull')}</cadence-button>
          <cadence-button variant="ghost" @click=${() => this.downloadItem(it)}>${t('portal.gallery.download')}</cadence-button>
          <cadence-button variant="ghost" @click=${() => (this.lightbox = null)}>${t('portal.gallery.close')}</cadence-button>
        </div>
      </cadence-modal>`;
  }
}
